// ══════════════════════════════════════════════════════
// Strategy Lab — Constants & Utilities
// ══════════════════════════════════════════════════════

import { API_BASE } from "../../config";
import { theme, fonts } from "../../theme";
// A-2: the ONE shared Form<->YAML mapping source, consumed by Python runtime
// (bot/api/param_registry.py) and this Builder. NO second copy of the table here.
import REGISTRY from "../../../../shared/param_registry.json";

/* ── Instruments ──────────────────────────────── */
export const ALL_INSTRUMENTS = ["MGC","MES","MNQ","MYM","M2K","SIL","MCL","MBT","FDXS"];
export const TFS = ["M1","M5","M15","M30","H1","H4","D"];

export const INST_META = {
  MGC:{exchange:"COMEX",tick:0.10,pv:10,margin:1100},
  MES:{exchange:"CME",tick:0.25,pv:5,margin:1600},
  MNQ:{exchange:"CME",tick:0.25,pv:2,margin:2100},
  MYM:{exchange:"CBOT",tick:1.0,pv:0.5,margin:900},
  M2K:{exchange:"CME",tick:0.10,pv:5,margin:700},
  SIL:{exchange:"COMEX",tick:0.005,pv:1000,margin:1700},
  MCL:{exchange:"NYMEX",tick:0.01,pv:100,margin:800},
  MBT:{exchange:"CME",tick:0.001,pv:1000,margin:600},
  FDXS:{exchange:"EUREX",tick:0.5,pv:5,margin:3000},
};

/* ── Strategy Profiles ───────────────────────── */
/* ── Strategy Profiles (goal-based, v3.7.5) ────
 * Each profile embeds:
 *   - strategy parameters (starting point — editable in form)
 *   - default goals (hard_constraints, monte_carlo config)
 *   - optimizer_range_overrides (narrow search space to profile's goal)
 * Custom has no values — user defines everything manually.
 */
export const STRATEGY_PROFILES = {
  prop_firm_safety: {
    label: "Prop-Firm Safety",
    desc: "Drawdown minimieren, Prop-Firm-Evaluations bestehen. Niedrige Risk, Profit Lock früh aktiv.",
    icon: "🛡️",
    // Strategy params
    entry_mode: "both", max_daily: 3, cooldown: 10,
    rr1: 2.5, rr2: 4.0, rr3: 6.0, rr4: 9.0, num_tp: 3,
    ctx_swing: 9, entry_swing: 6, pattern_age: 35,
    risk_pct: 0.3,
    // Goal defaults (will be written to YAML goals: block)
    goals_defaults: {
      // FIX K-2: Was "calmar" — a single metric that rewards trades-too-few overfits
      // (high Calmar from lucky winners + few losses). composite_score is the multi-
      // dimensional score in score_utils.calculate_composite_score, which balances
      // profitability, risk_control, consistency, and activity with synergy bonuses.
      // After K-1 removed the 1.0 cap, composite_score has a continuous gradient that
      // TPE can optimize against, discriminating "good" from "excellent" strategies.
      primary_objective: "composite_score",
      monte_carlo: { enabled: true, n_permutations: 1000 },
      hard_constraints_is: {
        min_trades: 50, min_profit_factor: 1.3, min_win_rate: 0.30,
        max_drawdown_pct: 5.0,
      },
      hard_constraints_oos: {
        min_profit_factor: 1.1, max_is_oos_degradation_pct: 40,
        max_dd_95pct_pct: 6.0,  // Monte-Carlo 95%-quantile DD limit
      },
    },
    optimizer_range_overrides: {
      risk_pct_min: 0.2, risk_pct_max: 0.5,
      sl_mult_min: 0.8, sl_mult_max: 1.8,
      be_rr_min: 0.5, be_rr_max: 1.5,
    },
  },
  daily_income: {
    label: "Daily Income",
    desc: "Konsistenter Cashflow, IBKR-Eigenkapital. Mehr Trades, mittlere TPs, akzeptiert höheren DD.",
    icon: "💵",
    entry_mode: "both", max_daily: 5, cooldown: 5,
    rr1: 2.0, rr2: 3.5, rr3: 5.0, rr4: 7.0, num_tp: 4,
    ctx_swing: 8, entry_swing: 5, pattern_age: 30,
    risk_pct: 0.6,
    goals_defaults: {
      // FIX K-2: Was "net_pnl" — the single most dangerous optimization target. It
      // rewards "one lucky mega-trade" strategies (overfit jackpot) because it ignores
      // consistency, risk, and sample size. Switch to composite_score for the same
      // reason as prop_firm_safety above.
      primary_objective: "composite_score",
      monte_carlo: { enabled: true, n_permutations: 500 },
      hard_constraints_is: {
        min_trades: 100, min_profit_factor: 1.2, min_win_rate: 0.35,
        max_drawdown_pct: 12.0,
      },
      hard_constraints_oos: {
        min_profit_factor: 1.0, max_is_oos_degradation_pct: 50,
      },
    },
    optimizer_range_overrides: {
      risk_pct_min: 0.4, risk_pct_max: 1.0,
    },
  },
  swing_compounding: {
    label: "Swing Compounding",
    desc: "Wenige große Gewinne, HTF-Setups. Hohe RR, seltene Trades, lange Halte-Dauer.",
    icon: "📈",
    entry_mode: "bos in trend", max_daily: 2, cooldown: 15,
    rr1: 4.0, rr2: 7.0, rr3: 11.0, rr4: 18.0, num_tp: 3,
    ctx_swing: 11, entry_swing: 7, pattern_age: 50,
    risk_pct: 0.5,
    goals_defaults: {
      // FIX K-2: Was "sortino" — rewards strategies with low downside variance, which
      // correlates with having few losers (can be overfit artifact of trend-filter
      // over-tuning). composite_score for robustness.
      primary_objective: "composite_score",
      monte_carlo: { enabled: true, n_permutations: 500 },
      hard_constraints_is: {
        min_trades: 30, min_profit_factor: 1.8, min_win_rate: 0.28,
        max_drawdown_pct: 15.0,
      },
      hard_constraints_oos: {
        min_profit_factor: 1.3, max_is_oos_degradation_pct: 45,
      },
    },
    optimizer_range_overrides: {
      tp1_min: 3.0, tp1_max: 6.0,
      tp4_min: 10.0, tp4_max: 20.0,
    },
  },
  scalp_momentum: {
    label: "Scalp Momentum",
    desc: "Hochfrequentes Scalping auf M1/M5, enge SLs, niedrige RR, viele Trades.",
    icon: "⚡",
    entry_mode: "single choch", max_daily: 8, cooldown: 2,
    rr1: 1.5, rr2: 2.5, rr3: 4.0, rr4: 6.0, num_tp: 4,
    ctx_swing: 6, entry_swing: 3, pattern_age: 20,
    risk_pct: 0.4,
    goals_defaults: {
      // FIX K-2: Was "profit_factor" — easiest metric to jackpot (reject trades until
      // only winners remain → PF inflates, trade-count implodes). composite_score
      // has sample-size disqualifiers built in.
      primary_objective: "composite_score",
      monte_carlo: { enabled: true, n_permutations: 1000 },
      hard_constraints_is: {
        min_trades: 200, min_profit_factor: 1.25, min_win_rate: 0.40,
        max_drawdown_pct: 10.0,
      },
      hard_constraints_oos: {
        min_profit_factor: 1.1, max_is_oos_degradation_pct: 40,
      },
    },
    optimizer_range_overrides: {
      sl_mult_min: 0.5, sl_mult_max: 1.2,
      tp1_min: 1.0, tp1_max: 2.5,
    },
  },
  custom: {
    label: "Custom",
    desc: "Leere Basis — alle Filter AUS, Single-TP, kein BE/Trail. Die Pipeline entscheidet alles.",
    icon: "🎨",
    // FIX R6-20: Custom now has explicit minimal-neutral strategy_params so the
    // wizard displays a truly empty canvas (not leftover MGC defaults from DEF_FORM).
    // Every decision is deferred to the Optimizer (Phase 1/A will sample them).
    strategy_params: {
      // R7b-5: Risk limits neutral — the optimizer will find the right values
      // in Phase 3. User can configure restrictions post-hoc.
      max_daily: 999, cooldown: 0,
      // Structure — generic symmetric defaults, optimizer will refine
      ctx_swing: 8, entry_swing: 5,
      break_confirm: "close",      // slight edge on noise reduction
      entry_mode: "both",           // let optimizer pick specific pattern
      direction_filter: "both",     // long AND short until proven otherwise
      pattern_age: 30, choch_age: 40,  // start without HTF gating
      // Stop Loss — structure-based (no magic ATR numbers)
      sl_mode: "structure_only",
      atr_len: 14, atr_min: 1.0, atr_max: 2.0,
      safety_floor_enabled: false,
      // Take Profit — simplest config: 1 TP at 2R, no partials
      tp_mode: "fixed_rr", num_tp_levels: 1,
      rr1: 2.0, rr2: 0, rr3: 0, rr4: 0,
      p1: 100, p2: 0, p3: 0, p4: 0,
      partial_trail_mode: "wait_for_all",
      trail_after_tp: 1,
      // Break Even — OFF (optimizer decides in Phase 2)
      be: false, be_mode: "rr", be_rr: 1.0, be_fixed_points: 0, be_offset_ticks: 0,
      // Trailing — OFF (optimizer decides in Phase 2)
      trail_mode: "none", trail_mult: 1.5, trail_rr: 1.5,
      // All filters OFF — optimizer decides
      regime: false, adx: 20, adx_length: 14, sma_len: 30,
      regime_atr_length: 14, regime_mode: "either",
      ema: false, ema_len: 20, ema_mode: "price_vs_ema",
      fvg: false, fvg_proximity_enabled: false,
      // Bad hours — empty by default (optimizer picks if trading window helps)
      bad_hours: [], bad_en: false,
      // Loss scaling — OFF (optimizer decides)
    },
    goals_defaults: null,  // No default goals — user sets manually
    optimizer_range_overrides: {},
  },
};

/* ── Brokers (Top-Level Dropdown) ────────────── */
export const BROKERS = {
  ibkr: {
    label: "IBKR", desc: "Interactive Brokers — freies Kapital, keine externen Limits", icon: "🏦",
    has_prop_rules: false,
  },
  topstep: {
    label: "Topstep", desc: "Prop-Firm — externe Loss-Limits, Scaling-Plan", icon: "🏆",
    has_prop_rules: true,
  },
  custom_prop: {
    label: "Custom Prop-Firm", desc: "Eigene Prop-Firm mit selbst-definierten Limits", icon: "⚙️",
    has_prop_rules: true,
  },
};

/* ── Prop-Firm Account Configurations (Sub-Section) ─── */
// Used only when broker.has_prop_rules = true. User selects one of these AFTER picking Topstep.
export const PROP_FIRM_ACCOUNTS = {
  topstep: {
    "50k_combine":  { label: "50K Combine",  account_size: 50000,  profit_target: 3000, max_loss_limit: 2000, daily_loss_limit: 1000, max_contracts: 5,  max_micros: 50,  consistency_rule: true,  phase: "combine" },
    "100k_combine": { label: "100K Combine", account_size: 100000, profit_target: 6000, max_loss_limit: 3000, daily_loss_limit: 2000, max_contracts: 10, max_micros: 100, consistency_rule: true,  phase: "combine" },
    "150k_combine": { label: "150K Combine", account_size: 150000, profit_target: 9000, max_loss_limit: 4500, daily_loss_limit: 3000, max_contracts: 15, max_micros: 150, consistency_rule: true,  phase: "combine" },
    "50k_express":  { label: "50K Express",  account_size: 50000,  profit_target: null, max_loss_limit: 2000, daily_loss_limit: 1000, scaling_plan: true,  phase: "express" },
    "100k_express": { label: "100K Express", account_size: 100000, profit_target: null, max_loss_limit: 3000, daily_loss_limit: 2000, scaling_plan: true,  phase: "express" },
    "150k_express": { label: "150K Express", account_size: 150000, profit_target: null, max_loss_limit: 4500, daily_loss_limit: 3000, scaling_plan: true,  phase: "express" },
    "live":         { label: "Live (funded)", account_size: null, profit_target: null, max_loss_limit: 1000, daily_loss_limit: null, dynamic_risk_expansion: true, phase: "live" },
  },
};

/* ── Deployment Environment (separate from strategy — affects connector only) ── */
export const DEPLOYMENT_ENVS = {
  paper: { label: "Paper Trading", desc: "Simulation, kein echtes Kapital", icon: "📝" },
  live:  { label: "Live Trading",  desc: "Echtes Kapital im Einsatz",       icon: "💰" },
};

// Legacy alias: kept for backwards-compat only. New code uses BROKERS + PROP_FIRM_ACCOUNTS + DEPLOYMENT_ENVS.
export const ACCOUNT_TYPES = {
  ibkr_paper:          { label: "IBKR Paper",          desc: "Kein Limit — freies Testen",  icon: "📝", broker: "ibkr", port: 4001 },
  ibkr_live:           { label: "IBKR Live",           desc: "Eigenes Kapital",              icon: "💰", broker: "ibkr", port: 4002 },
  topstep_50k_combine: { label: "Topstep 50K Combine", broker: "topstep", account_size: 50000, profit_target: 3000, max_loss_limit: 2000, daily_loss_limit: 1000, phase: "combine" },
  topstep_100k_combine:{ label: "Topstep 100K Combine",broker: "topstep", account_size: 100000, profit_target: 6000, max_loss_limit: 3000, daily_loss_limit: 2000, phase: "combine" },
  topstep_150k_combine:{ label: "Topstep 150K Combine",broker: "topstep", account_size: 150000, profit_target: 9000, max_loss_limit: 4500, daily_loss_limit: 3000, phase: "combine" },
  topstep_50k_express: { label: "Topstep 50K Express", broker: "topstep", account_size: 50000,  max_loss_limit: 2000, daily_loss_limit: 1000, phase: "express" },
  topstep_100k_express:{ label: "Topstep 100K Express",broker: "topstep", account_size: 100000, max_loss_limit: 3000, daily_loss_limit: 2000, phase: "express" },
  topstep_150k_express:{ label: "Topstep 150K Express",broker: "topstep", account_size: 150000, max_loss_limit: 4500, daily_loss_limit: 3000, phase: "express" },
  topstep_live:        { label: "Topstep Live",        broker: "topstep", phase: "live" },
  custom_prop:         { label: "Custom Prop-Firm",    broker: "custom" },
};
export const DEF_FORM = {
  inst:"MGC",entry_tf:"M5",ctx_tf:"H1",regime_tf:"H4",
  name:"",version:"1.0.0",status:"test",
  account_type:"ibkr_paper",
  risk_pct:0.4,account:50000,max_daily:999,cooldown:0,
  max_bars_in_trade:2000,  // unhardcoded (was fixed 2000)
  ctx_swing:9,entry_swing:6,break_confirm:"wick",
  entry_mode:"both",direction_filter:"both",num_tp_levels:4,
  pattern_age:30,choch_age:40,
  // Stop Loss — unhardcoded safety_floor
  sl_mode:"structure_then_atr",atr_len:13,atr_min:1.0,atr_max:1.3,
  safety_floor_enabled:false, safety_floor_atr:0.5, safety_floor_ticks:10,
  sl_ema_length:20,  // Welle-2 (only used when sl_mode=ema)
  // Take Profit — tp_mode + partial_trail_mode (Welle-2)
  tp_mode:"partial_trail",  // unhardcoded (was fixed partial_trail)
  partial_trail_mode:"wait_for_all",
  trail_after_tp:2,
  rr1:2.5,rr2:4.5,rr3:7.0,rr4:12.0,p1:35,p2:30,p3:25,p4:10,
  // Break Even — unhardcoded be_mode
  be:true, be_mode:"rr", be_rr:0.75, be_fixed_points:10, be_offset_ticks:0,
  // Trailing — unhardcoded trail_mode + trail_ema_length (Welle-2)
  trail_mode:"atr_trail", trail_mult:1.5, trail_rr:1.5,
  trail_ema_length:20,  // Welle-2 (only used when trail_mode=ema_trail)
  // Regime — unhardcoded adx_length + atr_length
  regime:true, adx:15, adx_length:14, sma_len:30, regime_atr_length:14, regime_mode:"either",
  ema:true,ema_len:5,ema_mode:"price_vs_ema",
  fvg:false,
  // Welle-3 FVG Proximity
  fvg_proximity_enabled:false,
  bad_hours:[],bad_en:true,
  // Prop-Firm Policy (Welle-1 + Welle-3, visible only for prop-firm accounts)
  max_daily_loss_pct:0,  // 0 = off, typical Topstep: 1.8
  profit_lock_threshold_pct:0,  // 0 = off, typical Topstep: 1.5
  profit_lock_factor:0.5,
  profile:"custom",
  goals:null  // Goals block — populated by profile or user
};

/* ── Optimizer Default Ranges ────────────────── */
// R7b-6: Expanded ranges so the optimizer has real search-space freedom
// across all timeframe regimes (M1 scalping → H4 swing). Narrow ranges
// like 1-8 trades/day or 0-20 cooldown-bars artificially limited the optimizer
// especially on higher TFs where Swing setups need longer cooldowns
// (30-60 bars = multiple days/weeks between trades).
export const DEFAULT_RANGES = {
  // v4: Nur die 11 parametrisch gesampelten Params + Toggle-Support-Ranges.
  // Alle v3-Ranges für hardcoded/removed Params entfernt (atr_length, ema2/3,
  // max_daily_trades, cooldown, max_bars_in_trade, loss_scale_*, sl_ema, trail_ema).

  // Risk (Dashboard-override only, nicht gesampelt)
  risk_pct_min:0.3, risk_pct_max:1.0,

  // Stop-Loss — single sl_atr_mult (collapsed in v4, kein min/max mehr)
  sl_atr_mult_min:0.75, sl_atr_mult_max:2.5, sl_atr_mult_step:0.25,
  atr_floor_mult_min:0.3, atr_floor_mult_max:1.5, atr_floor_mult_step:0.15,

  // Take-Profit RR
  tp1_rr_min:1.5, tp1_rr_max:5.0, tp1_rr_step:0.25,
  tp2_rr_min:3.0, tp2_rr_max:10.0, tp2_rr_step:0.5,

  // Break-Even (gated by be_enabled)
  be_trigger_rr_min:0.25, be_trigger_rr_max:2.5, be_trigger_rr_step:0.25,

  // Swings (categorical — diskrete Fractal-Buckets)
  entry_swing_choices:[3,4,5,6,7,8,9,10],
  context_swing_choices:[5,6,7,8,9,10,12,15,20],

  // Pattern-Ages (categorical — cliff-basierte Buckets)
  max_pattern_age_choices:[15,25,40,60,90],
  context_choch_max_age_choices:[20,40,80,150],

  // Regime ADX threshold (gated by regime_enabled, categorical)
  adx_threshold_choices:[18,22,26,30],

  // Partial-Pcts Presets (gated by tp_mode = partial_trail_2tp)
  partial_pcts_presets:["50/50","60/40","40/60"],
};

/* ── Pipeline Steps ──────────────────────────── */
export const PIPELINE_STEPS = [
  { id:"builder",   n:1, label:"Erstellen",   desc:"Strategie aus Template erstellen (Struktur gelockt)", icon:"🔧" },
  { id:"backtest",  n:2, label:"Baseline",    desc:"Baseline-Backtest mit Default-Params", icon:"📊" },
  { id:"optimizer", n:3, label:"Tuning",      desc:"Parameter-Tuning mit bucket_robust_score", icon:"⚡" },
  { id:"walkforward", n:4, label:"Walk-Forward", desc:"OOS-Validierung mit hard_constraints_oos", icon:"🛡️" },
  { id:"cross", n:5, label:"Cross-Instrument", desc:"Validierung auf MNQ + FDXS", icon:"🔀" },
];

export const PIPELINE_STATUS_ORDER = ["created","tuned","walkforwarded","cross_validated","live"];

/* ── Phase Config ────────────────────────────── */
// R7c-6: Each phase now has min/default/sweet/max + reasoning fields.
// Defaults set to Sweet-Spot values (was previously Minimum-values).
// Guidelines derived from TPE-sampler convergence: ~30 × log(search_space_dimensions)
// trials needed for statistical convergence. Over `max` = diminishing returns + overfit risk.
export const OPTIMIZER_PHASES = {
  // v4: single mode only. Struktur ist YAML-locked, Optimizer tuned nur Parameter.
  tuning: {
    label: "Parameter Tuning",
    shortLabel: "Tuning",
    trials: 1000,           // default for MGC 4-year IS window
    trials_min: 500,        // below this: unreliable Best-Trial
    trials_sweet: 1000,     // sweet spot for 11 parametric + 5 toggle dims
    trials_max: 3000,       // diminishing returns beyond this
    desc: "Tuned die 11 parametrischen Werte (sl_atr_mult, atr_floor, tp1/tp2_rr, swings, pattern-ages, adx_threshold, partial_pcts) + 5 Feature-Toggles (be, ema, fvg, regime, tp_mode). Struktur ist YAML-locked.",
    reasoning: "Mittlerer Suchraum (~32 Sub-Hypothesen × ~10^6 numerische Kombinationen mit Step-Quantisierung). TPE konvergiert bei 1000 Trials zuverlässig.",
    input: "Locked YAML-Template mit goals.ranges + hard_constraints_is/oos",
    output: "Best-Params + Monte-Carlo Deploy-Ready-Check + Post-Hoc-Session-Recommendation",
    action: "Walk-Forward starten → Cross-Instrument-Validation",
    color: theme.green,
  },
};

/* ── Utils ────────────────────────────────────── */
export const ok = d => d && typeof d === "object" && !d.detail;

export const fDate = ts => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleDateString("de-DE", { day:"numeric", month:"short", year:"2-digit", hour:"2-digit", minute:"2-digit" }); }
  catch { return "—"; }
};

export const fDateShort = ts => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleDateString("de-DE", { day:"numeric", month:"short" }); }
  catch { return "—"; }
};

export const postApi = async (path, body) => {
  try {
    const r = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body || {})
    });
    return await r.json();
  } catch (e) { console.error(e); return null; }
};

export const getApi = async path => {
  try {
    const r = await fetch(`${API_BASE}${path}?_t=${Date.now()}`);
    return await r.json();
  } catch (e) { console.error(e); return null; }
};

export const deleteApi = async path => {
  try {
    const r = await fetch(`${API_BASE}${path}`, { method: "DELETE" });
    return await r.json();
  } catch (e) { console.error(e); return null; }
};

/* ── YAML Roundtrip ──────────────────────────── */
/* ── A-2 Phase 3a — shared-registry-driven simple fields ──────────────────────
 * The SIMPLE single-path form fields (path + default from shared/param_registry.json)
 * are loaded/saved by the two helpers below. Same Membership-Pattern as the Python
 * translators: the code declares the scope (FORM_SIMPLE_FIELDS), the registry supplies
 * yaml_path + default. PROCEDURAL fields stay inline in parseYamlToForm/formToConfig:
 * aliases (account, be_rr, trail_mult), gated sub-blocks (safety_floor, ema_length,
 * ema2/3), boolean ===true/!==false coercion (be/ema/fvg/regime/mtf_enabled/bad_en/…),
 * string builders (partial_pcts), prop_firm/goals/name-generation, the regime_filter
 * block (load-asymmetric, out of scope), and the D2 hardcodes (tp_mode/num_levels/
 * be_mode + orphaned rr3/rr4/p4/be_fixed_points) — those are flipped in Phase 3b.
 * Validated: registry.default === legacy ??-default AND yaml_path === legacy path for
 * every entry → Phase 3a roundtrip is byte-identical (value-equal). */
const _REG_BY_FORM = (() => {
  const m = {};
  for (const s of REGISTRY.params) if (s.form_field && !(s.form_field in m)) m[s.form_field] = s;
  return m;
})();

const FORM_SIMPLE_FIELDS = [
  "risk_pct", "max_daily", "cooldown", "max_bars_in_trade", "min_sl_points",
  "min_trade_qty", "max_contracts", "commission_per_contract", "spread_ticks",
  "ctx_swing", "entry_swing", "break_confirm", "entry_mode", "direction_filter",
  "pattern_age", "choch_age",
  "sl_mode", "atr_len", "atr_min", "atr_max",
  "rr1", "rr2", "p1", "p2", "p3", "runner_trail_atr_mult",
  "be_offset_ticks", "post_be_trail_atr_mult", "post_be_trail_min_r",
  "trail_mode", "trail_rr",
  "mtf_daily_atr", "mtf_daily_bars", "mtf_h1_atr", "mtf_h1_bars",
  "ema_len", "ema_mode",
];

function _getNested(obj, path) {
  let cur = obj;
  for (const k of path.split(".")) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = cur[k];
  }
  return cur;
}
function _setNested(obj, path, val) {
  const parts = path.split(".");
  let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) cur = (cur[parts[i]] ??= {});
  cur[parts[parts.length - 1]] = val;
}
// yaml -> form: form[field] = cfg<yaml_path> ?? registry.default
function applyRegistryLoad(form, cfg) {
  for (const f of FORM_SIMPLE_FIELDS) {
    const spec = _REG_BY_FORM[f];
    form[f] = _getNested(cfg, spec.yaml_path) ?? spec.default;
  }
  return form;
}
// form -> yaml: cfg<yaml_path> = form[field]
function applyRegistrySave(cfg, form) {
  for (const f of FORM_SIMPLE_FIELDS) {
    const spec = _REG_BY_FORM[f];
    _setNested(cfg, spec.yaml_path, form[f]);
  }
  return cfg;
}

export function parseYamlToForm(cfg) {
  if (!cfg || typeof cfg !== "object") return null;
  const i=cfg.instrument||{},r=cfg.risk||{},s=cfg.structure||{},sl=cfg.stop_loss||{},
    tp=cfg.take_profit||{},be=cfg.break_even||{},tr=cfg.trailing||{},
    rf=cfg.regime_filter||{},ef=cfg.ema_filter||{},bh=cfg.bad_hours||{},
    ls=cfg.loss_scaling||{},tfs=cfg.timeframes||{},mtf=cfg.multi_tf_gate||{};
  const en=cfg.entry||{};
  const sfEnabled = sl.safety_floor_enabled ?? true;
  const dtr = (r.regime_filter||{}).daily_dtr_filter || {};
  const ssp = r.sl_streak_protection || {};
  // PROCEDURAL fields only — the 37 simple ones are added by applyRegistryLoad below.
  const form = {
    inst:i.symbol||"MGC", entry_tf:tfs.entry||"M5",
    ctx_tf:tfs.context||tfs.entry||"M5", regime_tf:tfs.regime||"H4",
    name:cfg.strategy_name||"", version:cfg.version||"1.0.0",
    status:cfg.status||"test",
    account:r.initial_capital??r.account_size??50000,   // alias chain — procedural
    // SL Streak Protection
    sl_streak_enabled:ssp.enabled===true,
    sl_streak_threshold:ssp.consecutive_sl_threshold??3,
    sl_streak_pause_hours:ssp.pause_hours??24,
    sl_streak_reset_on_winner:ssp.reset_on_winner!==false,
    // DTR Filter (Volatility filter — replaces old ADX regime)
    dtr_enabled:dtr.enabled!==false,
    dtr_lookback:dtr.lookback_days??120,
    dtr_percentile:dtr.threshold_percentile??99.0,
    // Regime filter (top-level ADX/ATR) — read back from YAML, symmetric to the
    // formToConfig save below (was load-asymmetric: block written from form-defaults,
    // never read → Builder save misrepresented regime state + clamped adx/atr_length).
    // Default OFF (===true) to match the engine (engine_fast.py: regime.get("enabled",
    // False)) and the validated v7 family — NOT DEF_FORM's new-strategy regime:true.
    regime:rf.enabled===true, regime_mode:rf.mode||"either", adx:rf.adx_threshold??15,
    adx_length:rf.adx_length??14, regime_atr_length:rf.atr_length??14, sma_len:rf.atr_sma_length??30,
    num_tp_levels:tp.num_levels??2,
    // Stop Loss — safety_floor sub-block is gated (procedural)
    safety_floor_enabled:sfEnabled, safety_floor_atr:sl.safety_floor_atr??0.5,
    safety_floor_ticks:sl.safety_floor_ticks??10, sl_ema_length:sl.ema_length??20,
    // Take Profit — tp_mode/partial_trail_mode + rr3/rr4/p4 are D2 (Phase 3b)
    tp_mode:tp.mode||"partial_trail_with_runner",
    partial_trail_mode:tp.partial_trail_mode||"activate_trail",
    trail_after_tp:tp.trail_after_tp??2,
    rr3:tp.rr3??0, rr4:tp.rr4??0, p4:tp.partial4_pct??0,   // D2: p4 read from YAML (was hardcoded 0)
    partial_preset: tp.partial_pcts || "20/40/40",
    // Break Even — be_rr alias + be_mode/be_fixed_points (D2) are procedural
    be:be.enabled!==false, be_mode:be.mode||"activate_trail",
    be_rr:be.after_rr??be.trigger_rr??2.75,
    be_fixed_points:be.fixed_points??10,
    // Trailing — trail_mult alias + trail_ema_length gate stay procedural
    trail_mult:be.post_be_trail_atr_mult??tp.runner_trail_atr_mult??2.5,
    trail_ema_length:tr.ema_length??20,
    // Multi-TF Gate
    mtf_enabled:mtf.enabled!==false,
    // EMA filter — enabled coercion + ema2/3 gate stay procedural
    ema:ef.enabled===true,
    ema2_length:ef.ema2_length??0, ema3_length:ef.ema3_length??0,
    // FVG — D1-JS: engine reads fvg_filter first (canonical), legacy fvg as fallback
    fvg:(cfg.fvg_filter||cfg.fvg||{}).enabled===true, fvg_proximity_enabled:en.fvg_proximity_enabled===true,
    bad_hours:bh.hours||[], bad_en:bh.enabled!==false,
    max_daily_loss_pct:r.max_daily_loss_pct??0,
    profit_lock_threshold_pct:r.profit_lock_threshold_pct??0,
    profit_lock_factor:r.profit_lock_factor??0.5,
    goals: cfg.goals || null,
    // Ranges (from goals.ranges — the optimizer search space)
    ranges: cfg.goals?.ranges || {},
    profile: cfg._profile_name || "custom"
  };
  return applyRegistryLoad(form, cfg);
}

export function formToConfig(form) {
  const m = INST_META[form.inst] || INST_META.MGC;
  const isPropFirm = form.account_type && form.account_type !== "ibkr_paper" && form.account_type !== "ibkr_live";
  // Prop-Firm policy params — only set when user has entered positive values (implicit toggle via value > 0)
  const propFirmRisk = isPropFirm && (form.max_daily_loss_pct > 0 || form.profit_lock_threshold_pct > 0);
  // PROCEDURAL keys only — the 37 simple fields are written by applyRegistrySave below.
  // D2 hardcodes (take_profit.mode/num_levels, break_even.mode) and the legacy `fvg`
  // path are kept AS-IS in Phase 3a; they flip in Phase 3b.
  const cfg = {
    strategy_name: form.name || `SMC_MTF_${form.inst}_${form.entry_tf}`,
    version: form.version, status: form.status,
    instrument: {
      symbol:form.inst, exchange:m.exchange, sec_type:"FUT",
      currency:form.inst==="FDXS"?"EUR":"USD",
      tick_size:m.tick, point_value:m.pv,
    },
    timeframes: { entry:form.entry_tf, context:form.ctx_tf||form.entry_tf, regime:form.regime_tf },
    broker: { type: isPropFirm ? "topstepx" : "ibkr", client_id:3 },
    risk: {
      initial_capital:form.account,
      daily_reset_hour_utc:22,
      sl_streak_protection:{
        enabled:form.sl_streak_enabled||false,
        consecutive_sl_threshold:form.sl_streak_threshold??3,
        pause_hours:form.sl_streak_pause_hours??24,
        reset_on_winner:form.sl_streak_reset_on_winner!==false,
        min_winner_pnl_for_reset:100.0,
      },
      regime_filter:{
        daily_dtr_filter:{
          enabled:form.dtr_enabled!==false,
          lookback_days:form.dtr_lookback??120,
          threshold_percentile:form.dtr_percentile??99.0,
        },
        rolling_mfe_mae_filter:{ enabled:false, history_size:30, trigger_rr:0.5, resume_rr:1.0 },
      },
      ...(propFirmRisk ? {
        ...(form.max_daily_loss_pct > 0 ? {max_daily_loss_pct: form.max_daily_loss_pct} : {}),
        ...(form.profit_lock_threshold_pct > 0 ? {
          profit_lock_threshold_pct: form.profit_lock_threshold_pct,
          profit_lock_factor: form.profit_lock_factor ?? 0.5,
        } : {}),
      } : {})
    },
    // D1-JS: write the canonical fvg_filter block (engine reads it first); the legacy
    // `fvg` block is no longer written. Requires "fvg_filter" in FORM_OWNED_PATHS (backend).
    fvg_filter: { enabled:form.fvg },
    // Welle-3 entry-filter extensions
    entry: {
      ...(form.fvg_proximity_enabled ? {fvg_proximity_enabled: true} : {}),
    },
    stop_loss: {
      // Welle-1: canonical key, engine alias still accepts legacy `safety_floor`
      // D3: default aligned to ??true (matches parseYamlToForm load-default → no latent flip)
      safety_floor_enabled:form.safety_floor_enabled ?? true,
      ...(form.safety_floor_enabled ? {
        safety_floor_atr: form.safety_floor_atr ?? 0.5,
        safety_floor_ticks: form.safety_floor_ticks ?? 10,
      } : {}),
      // Welle-2: dedicated EMA length for sl_mode=ema
      ...(form.sl_mode === "ema" ? {ema_length: form.sl_ema_length ?? 20} : {}),
    },
    take_profit: {
      // D2: tp_mode + num_levels from the form (were hardcoded partial_trail_with_runner/2)
      mode:form.tp_mode,
      num_levels:form.num_tp_levels,
      partial_pcts:`${form.p1}/${form.p2}/${form.p3||0}`,
      // D2 (gated): rr3/rr4/p4 written only when the level count uses them (mirrors the
      // optimizer's num_tp zero-out cascade); 2-TP configs stay clean.
      ...(form.num_tp_levels >= 3 ? {rr3: form.rr3} : {}),
      ...(form.num_tp_levels >= 4 ? {rr4: form.rr4, partial4_pct: form.p4} : {}),
      runner_enabled:true,
    },
    break_even: {
      enabled:form.be,
      // D2: be_mode from the form (was hardcoded activate_trail)
      mode:form.be_mode,
      after_rr:form.be_rr,
      // D2 (gated): fixed_points only when the mode actually uses it
      ...(form.be_mode === "fixed_points" ? {fixed_points: form.be_fixed_points} : {}),
    },
    multi_tf_gate: {
      enabled:form.mtf_enabled!==false,
      daily_fractal:3, // Ghost-Parameter — fixiert
      h1_fractal:3,    // Ghost-Parameter — fixiert
    },
    trailing: {
      atr_mult:form.trail_mult,
      // Welle-2: dedicated EMA length for trail_mode=ema_trail
      ...(form.trail_mode === "ema_trail" ? {ema_length: form.trail_ema_length ?? 20} : {}),
    },
    regime_filter: {
      enabled:form.regime, mode:form.regime_mode,
      // Unhardcoded adx_length + atr_length (both were 14 hardcoded)
      adx_length:form.adx_length ?? 14, adx_threshold:form.adx,
      atr_length:form.regime_atr_length ?? 14, atr_sma_length:form.sma_len
    },
    ema_filter: {
      enabled:form.ema,
      ...(form.ema2_length > 0 ? {ema2_length: form.ema2_length} : {}),
      ...(form.ema3_length > 0 ? {ema3_length: form.ema3_length} : {}),
    },
    bad_hours: { enabled:form.bad_en, hours:form.bad_hours },
    loss_scaling: { enabled: false },  // v4: forced off
    ...(isPropFirm ? {
      prop_firm: (() => {
        const at = ACCOUNT_TYPES[form.account_type];
        if (!at) return undefined;
        return {
          type: form.account_type,
          broker: at.broker,
          account_size: at.account_size || form.account,
          profit_target: at.profit_target || form.prop_profit_target || null,
          max_loss_limit: at.max_loss_limit || form.prop_max_loss || null,
          daily_loss_limit: at.daily_loss_limit || form.prop_daily_loss || null,
          max_contracts: at.max_contracts || form.prop_max_contracts || null,
          mll_type: at.mll_type || "eod_trailing",
          consistency_rule: at.consistency_rule || false,
          scaling_plan: at.scaling_plan || false,
          phase: at.phase || "combine",
        };
      })()
    } : {}),
    // Goals block (v3.7.5) — from profile defaults or user-defined
    ...(form.goals ? { goals: { ...form.goals, ranges: form.ranges || form.goals?.ranges || {} } } : (() => {
      const prof = STRATEGY_PROFILES[form.profile];
      if (prof && prof.goals_defaults) return { goals: prof.goals_defaults };
      return {};
    })())
  };
  // A-2 Phase 3a: write the 37 simple fields by registry yaml_path (path from
  // shared/param_registry.json, value from the form). Procedural keys above stay inline.
  return applyRegistrySave(cfg, form);
}

export function validateForm(form) {
  const errs = [];
  // Existing rules
  if (form.risk_pct <= 0 || form.risk_pct > 5) errs.push("Risk % muss zwischen 0.1 und 5 liegen");
  // TP-Level checks — only require ordering if tp_mode uses multiple TPs AND num_tp_levels > 1
  const usesMultipleTPs = (form.tp_mode === "partial_trail" || !form.tp_mode) && (form.num_tp_levels || 4) > 1;
  if (usesMultipleTPs) {
    const ntp = form.num_tp_levels || 4;
    if (ntp >= 2 && form.rr1 >= form.rr2) errs.push("TP-Levels müssen aufsteigend sein: RR1 < RR2");
    if (ntp >= 3 && form.rr2 >= form.rr3) errs.push("TP-Levels müssen aufsteigend sein: RR2 < RR3");
    if (ntp >= 4 && form.rr3 >= form.rr4) errs.push("TP-Levels müssen aufsteigend sein: RR3 < RR4");
    // Partial-Summe: bei partial_trail mit Runner zaehlt der Runner-Anteil mit.
    // num_tp_levels zaehlt nur die TP-Levels; der Runner sitzt im naechsten
    // p-Slot dahinter. Wir summieren daher ALLE belegten Anteile (p1..p4),
    // nicht nur die ersten ntp — sonst wird der Runner faelschlich abgeschnitten.
    const allPcts = [form.p1, form.p2, form.p3, form.p4].map(x => Number(x) || 0);
    const pSum = allPcts.reduce((a,b) => a+b, 0);
    const partLabel = form.runner_enabled === false ? `${ntp} TPs` : `${ntp} TPs + Runner`;
    if (Math.abs(pSum - 100) > 1) errs.push(`Partial-Summe (${partLabel}) = ${pSum}%, muss 100% ergeben`);
  }
  if (form.entry_swing > form.ctx_swing) errs.push("Entry Swing darf nicht größer als Context Swing sein");

  // TF ordering (Entry ≤ Context ≤ Regime) — optimizer enforces this, builder should warn early
  const TF_RANK = {M1:1,M5:2,M15:3,M30:4,H1:5,H4:6,D:7,D1:7};
  const eRank = TF_RANK[form.entry_tf] ?? 1;
  const cRank = TF_RANK[form.ctx_tf] ?? 1;
  const rRank = TF_RANK[form.regime_tf] ?? 1;
  if (eRank > cRank) errs.push(`Entry TF (${form.entry_tf}) muss ≤ Context TF (${form.ctx_tf}) sein`);
  if (cRank > rRank) errs.push(`Context TF (${form.ctx_tf}) muss ≤ Regime TF (${form.regime_tf}) sein`);

  // v4: Loss Scaling validation removed (feature off)

  // Instrument-specific min SL: sl_atr_mult_min × avg ATR shouldn't be below 1 tick
  // (rough sanity — can't compute real ATR here but catch obviously-bad configs)
  if (form.atr_min <= 0) errs.push("SL ATR Min muss > 0 sein");
  if (form.atr_max < form.atr_min) errs.push("SL ATR Max muss ≥ SL ATR Min sein");

  // Welle-1/3 Prop-Firm policy — only for prop-firm accounts, plausible values
  const isPropFirm = form.account_type && !["ibkr_paper", "ibkr_live"].includes(form.account_type);
  if (isPropFirm) {
    if (form.max_daily_loss_pct < 0 || form.max_daily_loss_pct > 10)
      errs.push("Max Daily Loss % sollte in [0, 10] liegen (0 = off)");
    if (form.profit_lock_threshold_pct < 0 || form.profit_lock_threshold_pct > 50)
      errs.push("Profit Lock Threshold % sollte in [0, 50] liegen (0 = off)");
    if (form.profit_lock_threshold_pct > 0 && (form.profit_lock_factor <= 0 || form.profit_lock_factor > 1))
      errs.push("Profit Lock Factor muss in (0, 1] liegen");
  }

  // Welle-2 EMA lengths — plausible ranges (only relevant when mode uses EMA)
  if (form.sl_mode === "ema" && (form.sl_ema_length < 2 || form.sl_ema_length > 500))
    errs.push("SL EMA Length muss in [2, 500] liegen");
  if (form.trail_mode === "ema_trail" && (form.trail_ema_length < 2 || form.trail_ema_length > 500))
    errs.push("Trail EMA Length muss in [2, 500] liegen");

  // Regime — mode=adx_only requires non-zero adx_threshold, mode=atr_only requires non-zero atr_sma_length
  if (form.regime && (form.regime_mode === "adx_only" || form.regime_mode === "either" || form.regime_mode === "both")) {
    if (form.adx <= 0) errs.push(`Regime-Mode '${form.regime_mode}' braucht ADX Threshold > 0`);
  }

  // B7: Optimizer-Suchraum (goals.ranges) — min < max + plausible Werte (Save-Guard;
  //     ergänzt das sofortige Inline-Feedback in der Builder-RR-Komponente).
  const RANGE_FAMILIES = [
    ["SL ATR Mult","sl_atr_mult"], ["ATR Floor Mult","atr_floor_mult"],
    ["TP1 RR","tp1_rr"], ["TP2 RR","tp2_rr"], ["BE Trigger RR","be_trigger_rr"],
    ["Post-BE Trail ATR","post_be_trail_atr_mult"], ["Runner Trail ATR","runner_trail_atr_mult"],
  ];
  const rg = form.ranges || {};
  for (const [label, fam] of RANGE_FAMILIES) {
    const mn = rg[`${fam}_min`], mx = rg[`${fam}_max`], st = rg[`${fam}_step`];
    if (mn == null && mx == null) continue;            // Familie nicht gesetzt → Code-Default
    if (mn != null && Number(mn) <= 0) errs.push(`Range ${label}: Min muss > 0 sein`);
    if (mn != null && mx != null && Number(mx) <= Number(mn)) errs.push(`Range ${label}: Max (${mx}) muss > Min (${mn}) sein`);
    if (st != null && Number(st) <= 0) errs.push(`Range ${label}: Step muss > 0 sein`);
  }

  // C9: editierbare Advanced-goals-Felder — Paar-/Plausi-Validierung.
  const dc = form.goals?.monte_carlo?.deploy_constraints;
  if (dc) {
    const d95 = dc.max_dd_95pct_pct, d99 = dc.max_dd_99pct_pct;
    if (d95 != null && Number(d95) <= 0) errs.push("Deploy-Constraint: max_dd_95pct % muss > 0 sein");
    if (d95 != null && d99 != null && Number(d99) <= Number(d95))
      errs.push(`Deploy-Constraint: max_dd_99pct (${d99}%) muss > max_dd_95pct (${d95}%) sein`);
  }
  if (form.goals?.oos_consensus_top_n != null && Number(form.goals.oos_consensus_top_n) < 1)
    errs.push("oos_consensus_top_n muss ≥ 1 sein");

  return errs;
}

export function formToYamlString(form) {
  const c = formToConfig(form);
  const lines = [];
  const dump = (obj, indent=0) => {
    for (const [k,v] of Object.entries(obj)) {
      const pad = "  ".repeat(indent);
      if (v && typeof v === "object" && !Array.isArray(v)) {
        lines.push(`${pad}${k}:`);
        dump(v, indent+1);
      } else if (Array.isArray(v)) {
        lines.push(`${pad}${k}: [${v.join(", ")}]`);
      } else if (typeof v === "boolean") {
        lines.push(`${pad}${k}: ${v}`);
      } else if (typeof v === "string") {
        lines.push(`${pad}${k}: "${v}"`);
      } else {
        lines.push(`${pad}${k}: ${v}`);
      }
    }
  };
  dump(c);
  return lines.join("\n");
}

/* ── Best Params → Structured Apply Data ──────
 * Maps every Optuna-sampled key to its correct YAML section.
 * Previous version dropped ~10 enum/mode params and wrote risk-keys
 * into a dead "trading:" section. This version is the single source
 * of truth and should cover everything the optimizer can sample.
 * ───────────────────────────────────────────── */
export function bestParamsToApplyData(bp) {
  // Parse partial_pcts CSV ("35,30,25,10") → individual partial{1..4}_pct keys
  let p1, p2, p3, p4;
  if (typeof bp.partial_pcts === "string" && bp.partial_pcts.length) {
    const parts = bp.partial_pcts.split(",").map(s => Number(s.trim()));
    [p1, p2, p3, p4] = [parts[0], parts[1], parts[2], parts[3]];
  }

  const clean = {
    timeframes: bp.entry_tf ? {
      entry: bp.entry_tf,
      context: bp.context_tf,
      regime: bp.regime_tf,
    } : null,

    structure: {
      entry_mode: bp.entry_mode,
      break_confirm: bp.break_confirm,
      direction_filter: bp.direction_filter,
      context_swing_length: bp.context_swing_length,
      entry_swing_length: bp.entry_swing_length,
      max_pattern_age: bp.max_pattern_age,
      context_choch_max_age: bp.context_choch_max_age,
    },

    stop_loss: {
      mode: bp.sl_mode,
      atr_mult_min: bp.sl_atr_mult_min,
      atr_mult_max: bp.sl_atr_mult_max,
      atr_length: bp.atr_length,
      fixed_points: bp.sl_fixed_points,     // only set by optimizer when sl_mode=fixed_points
      ema_length: bp.sl_ema_length,         // only set by optimizer when sl_mode=ema
    },

    take_profit: {
      mode: bp.tp_mode,
      num_levels: bp.num_tp_levels,
      rr1: bp.tp1_rr,
      rr2: bp.tp2_rr,
      rr3: bp.tp3_rr,
      rr4: bp.tp4_rr,
      partial1_pct: p1,
      partial2_pct: p2,
      partial3_pct: p3,
      partial4_pct: p4,
      partial_trail_mode: bp.partial_trail_mode,
      trail_after_tp: bp.trail_after_tp,
    },

    break_even: {
      enabled: bp.be_enabled,
      mode: bp.be_mode,
      after_rr: bp.be_trigger_rr,
      fixed_points: bp.be_fixed_points,     // only set when be_mode=fixed_points
    },

    trailing: {
      mode: bp.trail_mode,
      atr_mult: bp.trailing_atr_mult,
      activation_rr: bp.trailing_activation_rr,
      ema_length: bp.trail_ema_length,      // only set when trail_mode=ema_trail
    },

    regime_filter: {
      enabled: bp.regime_enabled,
      mode: bp.regime_mode,
      adx_threshold: bp.adx_threshold,
      atr_sma_length: bp.atr_sma_length,
    },

    ema_filter: {
      enabled: bp.ema_enabled,
      mode: bp.ema_mode,
      ctx_ema1_length: bp.ctx_ema1_length,
      entry_ema1_length: bp.entry_ema1_length,
      // Engine default: entry_ema{i}_enabled = False. Optimizer sampling entry_ema1_length
      // without this flag would be a dead tune (value ignored). Auto-enable when sampled.
      entry_ema1_enabled: bp.entry_ema1_length != null ? true : undefined,
      // FIX K-28: ctx_ema1_enabled default in engine is now False (was True via i==1 quirk).
      // Auto-enable when ctx_ema1_length is sampled.
      ctx_ema1_enabled: bp.ctx_ema1_length != null ? true : undefined,
      // FIX K-39: Previously wrote ema2_length/ema3_length without ctx_ema{2,3}_enabled,
      // so the kernel ignored them (ctx_ema{i}_enabled defaults to False for i > 1).
      // Optimizer "found" an optimal ema2/3 length, but the applied YAML silently dropped
      // it — explaining part of the Optimizer-vs-Backtest PnL divergence. Now: when
      // length > 0 is present, also write the corresponding enable-flags for both
      // ctx and entry scope (matching the backtest_runner override logic).
      ...(bp.ema2_length != null && bp.ema2_length > 0 ? {
        ctx_ema2_enabled: true,
        ctx_ema2_length: bp.ema2_length,
        entry_ema2_enabled: true,
        entry_ema2_length: bp.ema2_length,
      } : { ema2_length: bp.ema2_length }),
      ...(bp.ema3_length != null && bp.ema3_length > 0 ? {
        ctx_ema3_enabled: true,
        ctx_ema3_length: bp.ema3_length,
        entry_ema3_enabled: true,
        entry_ema3_length: bp.ema3_length,
      } : { ema3_length: bp.ema3_length }),
    },

    fvg: {
      enabled: bp.fvg_enabled,
    },

    entry: {
      fvg_proximity_enabled: bp.fvg_proximity_enabled,
    },

    // FIX K-R3-1: section is "risk", not "trading" — engine reads from cfg.risk
    risk: {
      max_daily_trades: bp.max_daily_trades,
      cooldown_bars: bp.cooldown_bars,
      max_bars_in_trade: bp.max_bars_in_trade,
    },
    // v4: loss_scaling removed from bestParams mapper

    // bad_hours comes as Array from optimizer (empty list = no blocks). Apply only
    // when the key is present; backend-merges the `hours` array into existing section.
    bad_hours: Array.isArray(bp.bad_hours)
      ? { enabled: bp.bad_hours.length > 0, hours: bp.bad_hours }
      : null,
  };

  // Strip nulls/undefined at both section- and key-level
  const filtered = {};
  for (const [sec, vals] of Object.entries(clean)) {
    if (!vals) continue;
    const f2 = {};
    for (const [k, v] of Object.entries(vals)) {
      if (v != null) f2[k] = v;
    }
    if (Object.keys(f2).length) filtered[sec] = f2;
  }
  return filtered;
}

/* ── Consensus Builder ───────────────────────── */
export function buildConsensusItems(analysis, trialHistory) {
  if (!analysis) return { fix:[], narrow:[], open:[] };
  const fix=[], narrow=[], open=[];

  // Detect locked params: konstant über alle Trials → werden im Backend nicht optimiert,
  // sind also schon in der YAML fixiert. Diese aus Consensus rausnehmen.
  const lockedKeys = new Set();
  if (Array.isArray(trialHistory) && trialHistory.length >= 2) {
    const allKeys = new Set();
    trialHistory.forEach(t => Object.keys(t?.params || {}).forEach(k => allKeys.add(k)));
    for (const k of allKeys) {
      const first = trialHistory[0]?.params?.[k];
      const isConstant = trialHistory.every(t => {
        const v = t?.params?.[k];
        return v === first || (v == null && first == null);
      });
      if (isConstant) lockedKeys.add(k);
    }
  }

  // Step-Detection für numerische Params: schau alle distinkten Werte in trial_history an,
  // berechne den GGT der Differenzen → das ist der echte Step. Für Apply Consensus runden
  // wir Top-/Bottom-Mean und Range auf diesen Step, damit keine 3.945 in der YAML landen.
  function detectStep(key) {
    if (!Array.isArray(trialHistory)) return null;
    const vals = [...new Set(trialHistory.map(t => t?.params?.[key]).filter(v => typeof v === "number"))].sort((a,b)=>a-b);
    if (vals.length < 2) return null;
    let step = vals[1] - vals[0];
    for (let i = 2; i < vals.length; i++) {
      const d = vals[i] - vals[i-1];
      if (d < step && d > 0) step = d;
    }
    if (step <= 0) return null;
    // IEEE-754 cleanup: 0.5 - 0.45 = 0.04999999999999998, normalize by inspecting
    // significant decimals (max 4 expected for trading params).
    for (let dec = 0; dec <= 4; dec++) {
      const rounded = Number(step.toFixed(dec));
      if (Math.abs(rounded - step) < 1e-9) return rounded;
    }
    return Number(step.toFixed(4));
  }
  function snap(val, step) {
    if (val == null || step == null || step <= 0) return val;
    const snapped = Math.round(val / step) * step;
    // IEEE-754 cleanup: 18*0.05 = 0.9000000000000001 → round to step's decimal places.
    // decimals(0.05)=2, decimals(0.25)=2, decimals(1)=0, decimals(0.001)=3.
    const stepStr = String(step);
    const dot = stepStr.indexOf(".");
    const decimals = dot >= 0 ? stepStr.length - dot - 1 : 0;
    return Number(snapped.toFixed(decimals));
  }

  // Categorical: top_pick + confidence (backend uses top_pick, not recommendation)
  if (analysis.categorical) Object.entries(analysis.categorical||{}).forEach(([k,v]) => {
    if (!v) return;
    if (lockedKeys.has(k)) return;  // schon gelockt, nicht anbieten
    const val = v.top_pick ?? v.recommendation;
    if (v.confidence >= 80)
      fix.push({ key:k, value:val, confidence:v.confidence, type:"categorical", top:v.top_distribution });
    else
      open.push({ key:k, value:val, confidence:v.confidence, type:"categorical", top:v.top_distribution });
  });

  // Numerical: backend sends top_p10/top_p90. Auf echte Sample-Schritte runden, damit
  // "Apply Consensus" keine handeloszten Krumm-Werte (3.945) in YAML schreibt.
  if (analysis.numerical) Object.entries(analysis.numerical||{}).forEach(([k,v]) => {
    if (!v) return;
    if (lockedKeys.has(k)) return;  // konstant → schon fixiert
    const step = detectStep(k);
    const rawMin = v.top_p10 ?? v.recommended_min ?? v.min;
    const rawMax = v.top_p90 ?? v.recommended_max ?? v.max;
    const snappedMin = step ? snap(rawMin, step) : rawMin;
    const snappedMax = step ? snap(rawMax, step) : rawMax;
    // Wenn Min und Max nach Runden identisch sind (z.B. num_tp_levels [2-2]),
    // ist der Param effektiv konstant - kein Sinn ihn als "narrow" anzubieten.
    if (snappedMin === snappedMax) return;
    narrow.push({
      key:k,
      topMean:    step ? snap(v.top_mean,    step) : (v.top_mean    ?? v.topMean),
      allMean:    step ? snap(v.all_mean,    step) : (v.all_mean    ?? v.allMean),
      bottomMean: step ? snap(v.bot_mean ?? v.bottom_mean, step) : (v.bot_mean ?? v.bottom_mean ?? v.bottomMean),
      min:        snappedMin,
      max:        snappedMax,
      type:"numerical"
    });
  });

  return { fix, narrow, open };
}

/* ── Inline Styles (shared) ──────────────────── */
export const inp = {
  padding:"9px 13px", borderRadius:8, border:`1px solid ${theme.border}`,
  fontSize:14, fontFamily:fonts.mono, fontWeight:500,
  background:theme.white, color:theme.text, outline:"none",
  width:"100%", boxSizing:"border-box", transition:"border-color 0.15s,box-shadow 0.15s"
};
export const sel = { ...inp, cursor:"pointer", appearance:"auto" };
export const lbl = {
  fontSize:11, fontWeight:700, color:theme.text4,
  textTransform:"uppercase", letterSpacing:".7px",
  marginBottom:5, display:"block"
};
export const grpH = {
  fontSize:13, fontWeight:800, color:theme.text,
  textTransform:"uppercase", letterSpacing:"1px",
  padding:"14px 14px 6px", marginBottom:8, marginTop:4,
  marginLeft:-20, marginRight:-20, paddingLeft:20, paddingRight:20,
  background:"rgba(0,0,0,0.015)",
  borderTop:`1px solid ${theme.divider}`, borderBottom:`1px solid ${theme.divider}`
};
export const grpDesc = { fontSize:12, color:theme.text3, lineHeight:1.5, marginBottom:12, paddingLeft:2 };
